import React, { useState, useRef } from 'react'
import { Paper, IconButton } from '@material-ui/core'
import { Edit as EditIcon, Visibility } from '@material-ui/icons'
import {
  PagingState,
  CustomPaging,
  SortingState,
  FilteringState,
  IntegratedPaging,
  EditingState,
  IntegratedSorting,
  SelectionState,
} from '@devexpress/dx-react-grid'
import {
  Grid as TableGrid,
  Table,
  TableHeaderRow,
  TableColumnResizing,
  TableFilterRow,
  Toolbar,
  TableFixedColumns,
  DragDropProvider,
  ExportPanel,
  TableRowDetail,
  TableEditColumn,
  TableSelection,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui'
import { GridExporter } from '@devexpress/dx-react-grid-export'
import {
  StickyTable,
  ToolbarComponent,
  GridRoot,
  FilterRowCell,
  CellComponent,
  FilterIcon,
  FilterEditor,
  HeaderSortLabel,
} from './MyTableComponents'
import LoadingPanel from './LoadingPanel'
import { createRenderComponent, selectorFields } from '../../utils'
import { isSameDay, parseISO } from 'date-fns'

export default function TableComponent({
  columns, rows, loading, selectTimeSheet, isAdmin, selection, setSelection, setSelectionIds, realTimeFilters, setRealTimeFilters,
}) {
  const renderFilterRow = useRef(createRenderComponent())
  const [customSorting] = useState(columns.map(c => ({ columnName: c.name, compare: c.compare })))
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [columnWidths, setColumnWidths] = useState(
    columns.map(({ name, width }) => ({ columnName: name, width: width || 150 }))
  )

  const totalCount = rows.length
  const pageSizes = [5, 10, 25, 50]

  const handleFilterChanged = newFilter => {
    const foundFilterIdx = realTimeFilters.findIndex(filter => filter.columnName === newFilter.columnName)
    if (foundFilterIdx !== -1) {
      const filtersCopy = realTimeFilters.slice()
      if (
        newFilter.columnName === realTimeFilters[foundFilterIdx].columnName
          && Array.isArray(newFilter.value) ? !newFilter.value.length : !newFilter.value
      ) {
        filtersCopy.splice(foundFilterIdx, 1)
      } else {
        filtersCopy[foundFilterIdx] = newFilter
      }
      setRealTimeFilters(filtersCopy)
    } else {
      setRealTimeFilters(realTimeFilters.concat(newFilter))
    }
    setSelection([]);
    setSelectionIds([]);
  }

  const handleColumnWidthsChange = newColWidths => {
    setColumnWidths(newColWidths.map(col => {
      const foundMatch = columnWidths.find(c => c.columnName === col.columnName)
      return { ...col, wordWrapEnabled: foundMatch.wordWrapEnabled }
    }))
  }

  const renderEditButton = ({ row }) => {
    return (row.Submitted && !isAdmin) ? (
      <IconButton onClick={() => selectTimeSheet(row.TimeSheetID)}>
        <Visibility color="primary" />
      </IconButton>
    ) : (
      <IconButton onClick={() => selectTimeSheet(row.TimeSheetID)}>
        <EditIcon color="primary" />
      </IconButton>
    )
  }

  const applyFilters = row => {
    return !realTimeFilters.length || realTimeFilters.every(({ columnName, operator, value }) => {
      const colInfo = columns.find(c => c.name === columnName)
      if (Array.isArray(value)) {
        return value.some(v => filterEquality(v, operator, row, colInfo))
      } else {
        return filterEquality(value, operator, row, colInfo)
      }
    })
  }

  const filterEquality = (filter, operator, row, colInfo) => {
    const rowValue = colInfo.getCellValue ? colInfo.getCellValue(row) : row[colInfo.filterValueField || colInfo.name]
    if (selectorFields.includes(colInfo.type)) {
      return rowValue === filter.value
    }
    switch (colInfo.type) {
      case 'date':
        return isSameDay(parseISO(rowValue), parseISO(filter))
      case 'text':
        return rowValue && rowValue.toLowerCase().includes(filter.toLowerCase())
      default:
        return rowValue?.toString()?.toLowerCase() === filter?.toString()?.toLowerCase()
    }
  }

  function handleSetSelection(idxArr) {
    const idArr = [];
    for (const idx of idxArr) {
      const row = rows.filter(applyFilters)[idx];
      idArr.push(row.TimeSheetID);
    }
    setSelectionIds(idArr);
    setSelection(idxArr);
  }

  return (
    <Paper style={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: window.innerHeight - 160 }}>
      <TableGrid
        columns={columns}
        rows={rows.filter(applyFilters) || []}
        rootComponent={GridRoot}
      >
        <SelectionState
          selection={selection}
          onSelectionChange={handleSetSelection}
        />
        <DragDropProvider />
        <PagingState
          currentPage={page}
          onCurrentPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
        <CustomPaging
          totalCount={totalCount}
        />
        <SortingState
          defaultSorting={[{ columnName: 'TimeSheetID', direction: 'desc' }]}
        />
        <IntegratedSorting
          columnExtensions={customSorting}
        />
        <FilteringState
          filters={realTimeFilters}
          onFiltersChange={setRealTimeFilters}
        />
        <EditingState
          onCommitChanges={console.log}
        />
        <IntegratedPaging />
        <Table
          columnExtensions={columnWidths}
          tableComponent={StickyTable}
          cellComponent={(props) => <CellComponent {...{ ...props, isAdmin }} />}
        />
        <TableColumnResizing
          defaultColumnWidths={columnWidths}
          onColumnWidthsChange={handleColumnWidthsChange}
        />
        <TableHeaderRow
          sortLabelComponent={HeaderSortLabel}
          showSortingControls
        />
        <TableFilterRow
          showFilterSelector
          cellComponent={renderFilterRow.current(
            props => <FilterRowCell {...{ ...props, columns, handleFilterChanged }} />
          )}
          iconComponent={FilterIcon}
          editorComponent={FilterEditor}
        />
        <TableEditColumn
          showEditCommand
          width={48}
          cellComponent={props => (
            <TableEditColumn.Cell {...props} style={{ ...props.style, padding: 0 }}>
              {renderEditButton(props)}
            </TableEditColumn.Cell>
          )}
        />
        <TableSelection />
        <PagingPanel
          pageSizes={pageSizes}
        />
        <TableFixedColumns
          leftColumns={[TableRowDetail.COLUMN_TYPE, TableSelection.COLUMN_TYPE, TableEditColumn.COLUMN_TYPE]}
        />
        <Toolbar />
        {/*<ExportPanel startExport={handleStartExport} />*/}
        {loading && <LoadingPanel />}
      </TableGrid>

      {/*<GridExporter*/}
      {/*	ref={exporterRef}*/}
      {/*	rows={resource.flat()}*/}
      {/*	columns={exportCols}*/}
      {/*	getCellValue={customizeCell}*/}
      {/*	onSave={onSave(title)}*/}
      {/*/>*/}
    </Paper>
  )
}